import { useState, useRef, useEffect } from 'react'
import { Link, useNavigate } from 'react-router-dom'
import { useAuth } from '../../contexts/AuthContext'

export default function Header() {
  const navigate = useNavigate()
  const { user, isAuthenticated, signOut } = useAuth()
  const [showUserMenu, setShowUserMenu] = useState(false)
  const menuRef = useRef<HTMLDivElement>(null)

  useEffect(() => {
    function handleClickOutside(event: MouseEvent) {
      if (menuRef.current && !menuRef.current.contains(event.target as Node)) {
        setShowUserMenu(false)
      }
    }
    document.addEventListener('mousedown', handleClickOutside)
    return () => document.removeEventListener('mousedown', handleClickOutside)
  }, [])

  const handleSignOut = async () => {
    await signOut()
    setShowUserMenu(false)
    navigate('/')
  }

  return (
    <header className="sticky top-0 z-50 bg-background/90 backdrop-blur-xl border-b border-border-subtle">
      <div className="max-w-7xl mx-auto px-4 h-14 flex items-center justify-between">
        <div className="flex items-center gap-3 sm:gap-4">
          <Link to="/" className="flex items-center gap-2.5 group">
            <svg className="flex-shrink-0" width="32" height="32" viewBox="0 0 32 32" fill="none">
              <path d="M8 20C5.8 20 4 18.2 4 16C4 14.1 5.4 12.5 7.2 12.1C7.1 11.7 7 11.4 7 11C7 8.8 8.8 7 11 7C12.1 7 13.1 7.5 13.8 8.2C14.8 6.9 16.3 6 18 6C20.8 6 23 8.2 23 11C23 11.3 23 11.7 22.9 12C25.2 12.3 27 14.3 27 16.5C27 18.9 25 21 22.5 21" stroke="url(#headerMinimalGradient)" strokeWidth="2" strokeLinecap="round"/>
              <circle cx="25" cy="8" r="1.5" fill="#a78bfa"/>
              <defs>
                <linearGradient id="headerMinimalGradient" x1="4" y1="6" x2="27" y2="21">
                  <stop offset="0%" stopColor="#a78bfa"/>
                  <stop offset="100%" stopColor="#6366f1"/>
                </linearGradient>
              </defs>
            </svg>
            <span className="font-semibold text-base hidden sm:inline tracking-tight">
              DreamWright
            </span>
            <span className="font-semibold text-base sm:hidden tracking-tight">DW</span>
          </Link>
        </div>

        <div className="flex items-center gap-1">
          {!isAuthenticated ? (
            <Link
              to="/login"
              className="flex items-center gap-2 px-3.5 py-1.5 ml-1 rounded-lg bg-primary hover:bg-primary-dark text-white transition-all text-sm font-medium shadow-sm hover:shadow-md cursor-pointer"
            >
              <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 16l-4-4m0 0l4-4m-4 4h14m-5 4v1a3 3 0 01-3 3H6a3 3 0 01-3-3V7a3 3 0 013-3h7a3 3 0 013 3v1" />
              </svg>
              Sign in
            </Link>
          ) : user && (
            <div className="relative ml-1" ref={menuRef}>
              <button
                onClick={() => setShowUserMenu(!showUserMenu)}
                className="flex items-center justify-center w-9 h-9 rounded-lg hover:bg-surface-2 transition-all cursor-pointer"
                title={user.email || 'Account'}
              >
                {user.user_metadata?.avatar_url ? (
                  <img
                    src={user.user_metadata.avatar_url}
                    alt="Avatar"
                    className="w-8 h-8 rounded-lg"
                  />
                ) : (
                  <div className="w-8 h-8 rounded-lg bg-gradient-to-br from-accent to-primary flex items-center justify-center text-white text-sm font-semibold">
                    {user.email?.charAt(0).toUpperCase() || 'U'}
                  </div>
                )}
              </button>

              {showUserMenu && (
                <div className="absolute right-0 mt-2 w-52 bg-panel border border-border rounded-md shadow-2xl py-1 z-50 animate-scale-in">
                  <div className="px-4 py-3 border-b border-border-subtle">
                    <p className="text-sm font-medium truncate text-white">{user.user_metadata?.full_name || 'User'}</p>
                    <p className="text-xs text-muted-foreground truncate mt-0.5">{user.email}</p>
                  </div>
                  <button
                    onClick={handleSignOut}
                    className="w-full px-4 py-2.5 text-left text-sm text-red-400 hover:bg-red-500/10 transition-colors flex items-center gap-2.5 cursor-pointer"
                  >
                    <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 16l4-4m0 0l-4-4m4 4H7m6 4v1a3 3 0 01-3 3H6a3 3 0 01-3-3V7a3 3 0 013-3h4a3 3 0 013 3v1" />
                    </svg>
                    Sign out
                  </button>
                </div>
              )}
            </div>
          )}
        </div>
      </div>
    </header>
  )
}
